import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, FormattedMessage } from 'react-intl';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import {
	CommentType,
	type CommentData,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import type {
	Comment,
	CommentReply,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	handleDeepLinkNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import {
	scrollToElementWithViewingRoom,
	checkElementExpand,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { getLogger } from '@confluence/logger';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageContentId } from '@confluence/page-context';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { fg } from '@confluence/feature-gating';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { buildDefaultCommentsContentStatusFilters } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import type {
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQueryVariables as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables,
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment,
} from './__types__/InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery.ts';
import type { InlineCommentQueryParamsQuery_comments_nodes_location_InlineComment as InlineCommentLocation } from './graphql/__types__/InlineCommentQueryParamsQuery.ts';
import { useRetryOnMutation } from './useRetryOnMutation.ts';
import { createReplyToCommentParams, type AuthorWithAccountId } from './replyToCommentHelper.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { CommentQueryParamsListener } from './CommentQueryParamsListener.tsx';
// Type representing a comment with author field (both Comment and GraphQL query result have author)
type CommentWithAuthor = Pick<CommentData, 'author'> & {
	author?: {
		displayName?: string | null;
		accountId?: string | null;
	} | null;
};
const InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery = graphql`
	query InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
						}
					}
				}
				ancestors {
					id
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
				parentId
			}
		}
	}
`;
const logger = getLogger('inline-comments:query-param-listener');

export const i18n = defineMessages({
	commentLinkNotFoundFlagTitle: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.title',
		defaultMessage: 'This comment is not available',
		description:
			'The title for a warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
	commentLinkNotFoundFlagDescription: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.description',
		defaultMessage: 'It may be deleted.',
		description:
			'A warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
});

type InlineCommentQueryParamsListenerProps = {
	isArchived: boolean;
	rendererRef: React.RefObject<Element>;
};

type QueryParamData =
	| {
			location?: InlineCommentLocation;
			parentId?: string | null;
			parentLocation?: InlineCommentLocation;
	  }
	| undefined;

export const InlineCommentQueryParamsListenerComponent: React.FC<
	InlineCommentQueryParamsListenerProps & WithFlagsProps
> = ({ isArchived, rendererRef, flags }) => {
	const hasHandledCommentLink = useRef(false);
	const { showCommentsPanel } = useShowCommentsPanel();
	const { orderedActiveAnnotationIdList } = useCommentsDataState();
	const [contentId] = usePageContentId();
	const { focusedCommentId, replyToCommentId, editCommentId, isCommentNotFound } =
		useInlineCommentQueryParams({
			queryArchivedContent: isArchived,
		});

	const { rendererActions } = useRendererActions();
	const pageMode = useGetPageMode();

	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus, setReplyToCommentId } =
		useCommentsPanelActions();
	const commentId = focusedCommentId || replyToCommentId || editCommentId;

	// NEW FLOW: Use useCommentById hooks when experiment is enabled
	const skipNewFlow =
		!commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false);

	const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);

	const {
		comment: foundComment,
		loading: commentLoading,
		error: commentError,
	} = useCommentById({
		commentId: commentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip: skipNewFlow,
		},
	});

	const {
		comment: parentComment,
		loading: parentLoading,
		error: parentError,
	} = useCommentById({
		commentId: foundComment?.parentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip: skipNewFlow || !foundComment?.parentId,
		},
	});

	// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
	const { data, loading, error } = useQueryInterop<
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables
	>(InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});
	if (error) {
		logger.error`An error occurred when querying the focused comment status; ${error}`;

		// TODO: If this query fails, there isn't much we can do, so just track it with experience tracker?

		markErrorAsHandled(error);
	}

	const queryParamData: QueryParamData = useMemo(() => {
		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			// NEW FLOW: Extract data from useCommentById hooks
			if (!commentError && !commentLoading && foundComment) {
				const location = foundComment.location as InlineCommentLocation | undefined;
				const parentId = foundComment.parentId;

				const parentLocation =
					parentId && !parentError && !parentLoading && parentComment
						? (parentComment.location as InlineCommentLocation)
						: undefined;

				// Return the data if this is a root commment (it doesn't have a parent comment) and we
				// have just that comment's data or if it's a reply and we also have a parent comment
				// with valid data.  This prevents returning incomplete data while parent comment is
				// loading, which causes the dependent memoized items to be generated multiple times and in
				// turn causes multiple simulated annotation clicks when we only want one simulated click
				// after everything is loaded.
				if (!parentId || parentComment) {
					return {
						location,
						parentId,
						parentLocation,
					};
				}
			}

			return undefined;
		} else {
			// OLD FLOW: Extract data from GraphQL query
			if (!error && !loading && data) {
				const location = data.comments?.nodes?.[0]?.location as InlineCommentLocation | undefined;
				const parentId = data.comments?.nodes?.[0]?.parentId;
				const parentLocation = data.comments?.nodes?.[0]?.ancestors?.[0]?.location as
					| InlineCommentLocation
					| undefined;

				return {
					location,
					parentId,
					parentLocation,
				};
			}

			return undefined;
		}
		// Note that we only want to regenerate this data only when the data contents has changed, not
		// when just the reference to the data has changed. Otherwise, we will generate the data twice:
		// once when useCommentById()'s single comment query returns with data and a second time when
		// the useAllCommentsData()'s query completes with similar data but a different reference -
		// which causes the dependent memoized items to be generated multiple times which in turn
		// causes multiple simulated annotation clicks when we only want one simulated click.
		// Note that looking at the comment IDs is sufficient because we only want to open/scroll to
		// the comment once even if other pieces of the comment's data change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contentId,
		commentId,
		data,
		loading,
		error,
		foundComment?.id,
		commentLoading,
		commentError,
		parentComment?.id,
		parentLoading,
		parentError,
	]);

	const queryParamDataExt = useMemo(() => {
		const { location, parentLocation } = queryParamData || {};
		return location?.inlineMarkerRef
			? {
					annotationId: location.inlineMarkerRef,
					isResolved: Boolean(location.inlineResolveProperties?.resolved),
				}
			: {
					annotationId: parentLocation?.inlineMarkerRef,
					isResolved: Boolean(parentLocation?.inlineResolveProperties?.resolved),
				};
	}, [queryParamData]);

	// Create callback for handling replyToCommentId when comment is rendered
	const onReplyToCommentReady = useCallback(
		() => {
			if (!replyToCommentId) {
				return;
			}

			// Get comment data from either new flow or old flow
			const rawComment:
				| Comment
				| CommentReply
				| InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment
				| null
				| undefined = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? foundComment
				: data?.comments?.nodes?.[0];
			if (rawComment) {
				const commentWithAuthor = rawComment as CommentWithAuthor;

				// Get annotationId from the reply-to comment's location
				const annotationId = queryParamDataExt?.annotationId;
				// Set the thread key to the annotationId of the parent comment we're replying to
				if (annotationId) {
					setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
				}

				// Access author field - both types have this field
				const author = commentWithAuthor?.author;

				// Set up reply state
				setReplyToCommentId(
					createReplyToCommentParams(
						replyToCommentId,
						LocationValue.PANEL,
						author as AuthorWithAccountId | null | undefined,
						fg('confluence_frontend_content_wrapper'),
					),
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			replyToCommentId,
			foundComment,
			data,
			queryParamDataExt,
			setReplyToCommentId,
			setCurrentlySelectedThreadKey,
		],
	);

	const tryToFocusAndScrollActiveCommentCallback = useCallback(() => {
		return tryToFocusAndScrollActiveComment({
			parentId: queryParamData?.parentId,
			annotationId: queryParamDataExt?.annotationId || null,
			commentId: commentId || null,
		});
	}, [queryParamData, queryParamDataExt, commentId]);

	useRetryOnMutation({
		callback: tryToFocusAndScrollActiveCommentCallback,
		elementRef: rendererRef,
		skip: !commentId || !queryParamData || queryParamDataExt.isResolved,
	});

	useEffect(() => {
		if (!queryParamData || hasHandledCommentLink.current) {
			return;
		}

		const { location } = queryParamData;
		const { annotationId } = queryParamDataExt;

		if (location) {
			if (queryParamDataExt.isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				if (annotationId) {
					setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
					setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
				}
			} else if (annotationId && fg('confluence_frontend_new_dangling_comments_ux')) {
				// Comment exists in DB (location is truthy) but annotation mark may be gone from page content.
				// Check if annotation mark exists in renderer; if not, open DELETED_CONTENT panel.
				const annotationExists = rendererActions
					?.getAnnotationMarks()
					?.some((mark: any) => mark.attrs.id === annotationId);
				if (!annotationExists) {
					setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
					setCommentContainerToFocus(commentId);
					showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
					expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true) &&
						onReplyToCommentReady();
					hasHandledCommentLink.current = true;
					return;
				}
			}
		} else {
			// Wait for the comment query to finish before checking if annotation exists
			// This prevents race condition where we check before comment data is available
			// Only apply this check when the feature flag is enabled
			if (expValEquals('confluence_comment_improve_comment_deeplink', 'isEnabled', true)) {
				// Wait for renderer actions and comment query to finish (isCommentNotFound is undefined while query is loading)
				if (!rendererActions || (annotationId && isCommentNotFound === undefined)) {
					return;
				}
			}

			// Check if annotation exists in renderer only when we need to make the decision
			// Ensure rendererActions is available before accessing it
			if (
				!rendererActions &&
				expValEquals('confluence_comment_improve_comment_deeplink', 'isEnabled', true)
			) {
				return;
			}

			const annotationExists = rendererActions
				?.getAnnotationMarks()
				?.some((mark: any) => mark.attrs.id === annotationId);

			if (annotationId && !annotationExists && fg('confluence_frontend_new_dangling_comments_ux')) {
				setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE); // needed so that the view knows which thread to scroll to after rendering the threads
				setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
				showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
				expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true) &&
					onReplyToCommentReady();
			}

			hasHandledCommentLink.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryParamData, rendererActions, orderedActiveAnnotationIdList]);

	// Only display the "comment not found" flag once
	useEffect(() => {
		if (isCommentNotFound) {
			const VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID = `viewpage-comment-link-not-found-${commentId}`;
			void flags.showWarningFlag({
				id: VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID,
				title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
				description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
			});
		}
	}, [commentId, flags, isCommentNotFound]);

	return null;
};

const tryToFocusAndScrollActiveComment = ({
	parentId,
	annotationId,
	commentId,
}: {
	parentId: string | null | undefined;
	annotationId: string | null;
	commentId: string | null;
}): boolean => {
	const mark = document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`);

	if (mark) {
		const focusedMarkerRef = document.querySelector("[data-has-focus='true']");
		// If the already open container is showing the correct comment, don't send another click event to open it
		if (focusedMarkerRef?.id !== annotationId) {
			const emitter = getRendererAnnotationEventEmitter();
			checkElementExpand(mark, true);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [annotationId],
				eventTarget: mark,
			});
		}

		// Deep-link scrolling: unified navigation when we have a comment id; otherwise scroll to the mark
		// (replies skip mark-only scroll here — reply scrolling runs after the reply renders to avoid double scroll)
		if (commentId) {
			const success = handleDeepLinkNavigation({
				scrollBehavior: ScrollBehavior.INSTANT,
				context: NavigationContext.RENDERER,
				block: 'center',
				markElement: mark,
				commentId,
			});

			if (!success) {
				abortAll('new_interaction', `focused-comment-scroll`);
				scrollToElementWithViewingRoom({
					element: mark,
					useInstantScroll: true,
				});
			}
		} else if (!parentId) {
			abortAll('new_interaction', `focused-comment-scroll`);
			scrollToElementWithViewingRoom({
				element: mark,
				useInstantScroll: true,
			});
		}
	}

	return !!mark;
};

const withInlineCommentQueryParamUnification =
	(
		Component: React.FC<InlineCommentQueryParamsListenerProps & WithFlagsProps>,
	): React.FC<InlineCommentQueryParamsListenerProps & WithFlagsProps> =>
	(props) => {
		if (fg('confluence_comment_param_unification')) {
			return (
				<CommentQueryParamsListener isArchived={props.isArchived} rendererRef={props.rendererRef} />
			);
		}
		return <Component {...props} />;
	};

export const InlineCommentQueryParamsListener = withFlags(
	withInlineCommentQueryParamUnification(InlineCommentQueryParamsListenerComponent),
);
